import React, { useState, useEffect } from "react";
import axios from "../../api/api";
import { toast } from "react-toastify";

const SEOSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    siteName: "",
    siteTitle: "",
    siteDescription: "",
    siteKeywords: "",
    facebook: "",
    twitter: "",
    linkedin: "",
    instagram: "",
    youtube: "",
    email: "",
    phone: "",
    address: "",
    logo: "",
    favicon: "",
    ogImage: "",
    googleAnalyticsId: "",
    googleTagManagerId: "",
    canonicalUrl: "",
    headerScripts: "",
    footerScripts: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const resp = await axios.get("/api/settings");
      if (resp.data.success) {
        setSettings(resp.data.data);
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const resp = await axios.post("/api/settings", settings);
      if (resp.data.success) {
        toast.success("Settings saved successfully!");
      }
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 justify-center items-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24 duration-300">
        <div className="xl:w-full min-h-[calc(100vh-152px)] relative pb-14">
          <div className="grid grid-cols-1 gap-6">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">
              <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6">
                <h4 className="font-semibold text-xl text-gray-800 dark:text-gray-300">
                  Advanced SEO & Global Settings
                </h4>
              </div>
              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* General Meta */}
                  <div className="space-y-4">
                    <h5 className="font-bold text-lg border-b pb-2 dark:text-gray-200">Meta Information</h5>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Site Name</label>
                      <input type="text" name="siteName" value={settings.siteName} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Site Title</label>
                      <input type="text" name="siteTitle" value={settings.siteTitle} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meta Description</label>
                      <textarea name="siteDescription" value={settings.siteDescription} onChange={handleChange} rows="3" className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white"></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Keywords (comma separated)</label>
                      <input type="text" name="siteKeywords" value={settings.siteKeywords} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Canonical URL Base</label>
                      <input type="url" name="canonicalUrl" value={settings.canonicalUrl} onChange={handleChange} placeholder="https://excellent.com" className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="space-y-4">
                    <h5 className="font-bold text-lg border-b pb-2 dark:text-gray-200">Social Media Links</h5>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Facebook</label>
                      <input type="url" name="facebook" value={settings.facebook} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Twitter</label>
                      <input type="url" name="twitter" value={settings.twitter} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">LinkedIn</label>
                      <input type="url" name="linkedin" value={settings.linkedin} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instagram</label>
                      <input type="url" name="instagram" value={settings.instagram} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">YouTube</label>
                      <input type="url" name="youtube" value={settings.youtube} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                  </div>

                  {/* Tracking & Scripts */}
                  <div className="space-y-4">
                    <h5 className="font-bold text-lg border-b pb-2 dark:text-gray-200">Tracking & Assets</h5>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Google Analytics ID</label>
                      <input type="text" name="googleAnalyticsId" value={settings.googleAnalyticsId} onChange={handleChange} placeholder="UA-XXXXXXXXX-X" className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Google Tag Manager ID</label>
                      <input type="text" name="googleTagManagerId" value={settings.googleTagManagerId} onChange={handleChange} placeholder="GTM-XXXXXXX" className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Logo URL</label>
                      <input type="text" name="logo" value={settings.logo} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Favicon URL</label>
                      <input type="text" name="favicon" value={settings.favicon} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default OG Image URL</label>
                      <input type="text" name="ogImage" value={settings.ogImage} onChange={handleChange} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" />
                    </div>
                  </div>

                  {/* Custom Scripts */}
                  <div className="space-y-4">
                    <h5 className="font-bold text-lg border-b pb-2 dark:text-gray-200">Advanced Scripts</h5>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Header Scripts (e.g. meta verification)</label>
                      <textarea name="headerScripts" value={settings.headerScripts} onChange={handleChange} rows="4" className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Footer Scripts</label>
                      <textarea name="footerScripts" value={settings.footerScripts} onChange={handleChange} rows="4" className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white font-mono text-sm"></textarea>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button type="submit" disabled={saving} className="px-6 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 flex items-center">
                    {saving ? "Saving..." : "Save Settings"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOSettings;
